import { cn } from '~/src/lib/utils'

/**
 * Dupe's research launch date — used to compute cumulative time saved.
 * Adjust as needed when the feature officially launches.
 */
const RESEARCH_LAUNCH_DATE = new Date('2026-03-04T00:00:00Z')

/**
 * Average minutes of research time saved per query.
 * Used to compute the "total time saved" counter.
 */
const MINUTES_SAVED_PER_QUERY = 45
const QUERIES_PER_SECOND = 0.3 // approx rate to make counter feel live

function formatDuration(totalMinutes: number): string {
  const totalSeconds = Math.floor(totalMinutes * 60)
  const years = Math.floor(totalSeconds / (365.25 * 24 * 3600))
  const rem1 = totalSeconds % Math.floor(365.25 * 24 * 3600)
  const months = Math.floor(rem1 / (30.44 * 24 * 3600))
  const rem2 = rem1 % Math.floor(30.44 * 24 * 3600)
  const days = Math.floor(rem2 / (24 * 3600))
  const rem3 = rem2 % (24 * 3600)
  const hours = Math.floor(rem3 / 3600)

  const parts: string[] = []
  if (years > 0) parts.push(`${years}y`)
  if (months > 0) parts.push(`${months}mo`)
  if (days > 0) parts.push(`${days}d`)
  // Hours is the smallest unit shown (minutes intentionally omitted), and is
  // always present so the badge never renders empty.
  parts.push(`${hours}h`)

  return parts.join(' ')
}

/**
 * Cumulative research time saved by Dupe users, in minutes.
 *
 * Computed once per render (call this in `getStaticProps` so the result is
 * cached by ISR and the value is identical on the server and the client).
 * Do NOT call this on a ticking interval — a live-updating value causes
 * per-second layout shift in the badge.
 */
export function computeMinutesSaved(): number {
  const elapsedMs = Date.now() - RESEARCH_LAUNCH_DATE.getTime()
  const elapsedSeconds = elapsedMs / 1000
  const totalQueries = elapsedSeconds * QUERIES_PER_SECOND
  return totalQueries * MINUTES_SAVED_PER_QUERY
}

interface ResearchTimerBadgeProps {
  /** Cumulative minutes saved, computed server-side via {@link computeMinutesSaved}. */
  minutesSaved: number
  className?: string
  /** Label beneath the timer. Defaults to Figma copy. */
  label?: string
}

/**
 * Badge showing cumulative research time saved by Dupe users. Renders a
 * static, server-computed value (no client-side ticking) so it stays cached
 * and never shifts layout. Consists of:
 *   - A blue pill with a pulsing dot + formatted duration (down to hours)
 *   - A muted label below (e.g. "Total time saved by Dupe users")
 */
export function ResearchTimerBadge({
  minutesSaved,
  className,
  label = 'Total time saved by Dupe users',
}: ResearchTimerBadgeProps) {
  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      {/* Blue pill badge */}
      <div
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
          'bg-[#F0FEFF] dark:bg-[#0BDDEC]/10'
        )}
      >
        {/* Pulsing dot */}
        <span className='relative flex size-2 shrink-0'>
          <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0BDDEC] opacity-75' />
          <span className='relative inline-flex size-2 rounded-full bg-[#0BDDEC]' />
        </span>
        <span className='text-sm font-medium text-[#029DA2]'>
          {formatDuration(minutesSaved)}
        </span>
      </div>

      {/* Label */}
      <p className='text-sm leading-tight opacity-40'>{label}</p>
    </div>
  )
}
